import {
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
} from "core/consts";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { postPatientDocument } from "core/model/careseekers/patientDocumentTransfer";
import { bytesToBase64 } from "core/model/crypto";
import { openPdfInNewWindow } from "core/model/utils/urls";
import {
  Careseeker,
  Observation,
  Patient,
  PatientDocumentType,
  QueryProgressActive,
} from "core/types";
import { useHL7ImportStatusContext } from "dsl/atoms/HL7ImportStatusContext";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { CreateRehaPdfProps } from "dsl/ecosystems/PatientForms/RehabForms/useCreateRehaPdf";
import { CreateTransitionalCarePdfProps } from "dsl/ecosystems/PatientForms/useCreateTransitionalCarePdf";
import { CreateDischargePdfProps } from "dsl/ecosystems/RecareDocs/Modal/Forms/DischargeLetters/useCreateFormPdf";
import { useCallback, useState } from "react";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";

export function useDocumentHandling<
  T extends
    | CreateTransitionalCarePdfProps
    | CreateRehaPdfProps
    | CreateDischargePdfProps,
>({
  careseekerName,
  createPDFDocument,
  externalId,
  getExistingFileUrl,
  userId,
}: {
  careseekerName: NonNullable<Careseeker["name"]>;
  createPDFDocument: (data: T) => Promise<Uint8Array>;
  externalId: NonNullable<Patient["user_id"]>;
  getExistingFileUrl?: () => Promise<string | undefined>;
  userId: NonNullable<Patient["user_id"]>;
}) {
  const toast = useToast();
  const translations = useTranslations();
  const { trackEvent } = useTracking();
  const { on_premise_authorization_token, on_premise_domain_ssl } =
    useHL7ImportStatusContext();
  const [progressOpenPdf, setProgressOpenPdf] = useState<QueryProgressActive>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const [progressSendToHis, setProgressSendToHis] =
    useState<QueryProgressActive>(QUERY_PROGRESS_NOT_STARTED);
  const { isRedesignActive } = useIsDocsAndExtractActive();

  const handleOpenPdf = useCallback(
    async (data?: T): Promise<boolean> => {
      try {
        setProgressOpenPdf(QUERY_PROGRESS_PENDING);
        if (getExistingFileUrl) {
          const url = await getExistingFileUrl();
          if (url) {
            window.open(url, "_blank");
            setProgressOpenPdf(QUERY_PROGRESS_NOT_STARTED);
            return true;
          }
        }
        if (!data) {
          setProgressOpenPdf(QUERY_PROGRESS_NOT_STARTED);
          return false;
        }
        const pdfBytes = await createPDFDocument(data);
        openPdfInNewWindow(pdfBytes);
        setProgressOpenPdf(QUERY_PROGRESS_NOT_STARTED);
        return true;
      } catch (err) {
        console.error((err as Error).message);
        setProgressOpenPdf(QUERY_PROGRESS_NOT_STARTED);
        return false;
      }
    },
    [createPDFDocument, getExistingFileUrl],
  );

  const handleSendToHis = useCallback(
    async (
      data: T & {
        documentType: PatientDocumentType;
        observation?: Observation;
      },
    ): Promise<boolean> => {
      if (!data) return false;
      const { documentType, observation, ...rest } = data;

      try {
        if (!on_premise_domain_ssl) {
          throw new Error("'on_premise_domain_ssl' is required");
        }

        setProgressSendToHis(QUERY_PROGRESS_PENDING);
        let pdfBytes: Uint8Array;
        if (getExistingFileUrl) {
          const url = await getExistingFileUrl();
          if (!url) throw new Error("Failed to get existing file URL");
          const buffer = await fetch(url).then((r) => r.arrayBuffer());
          pdfBytes = new Uint8Array(buffer);
        } else {
          pdfBytes = await createPDFDocument(rest as unknown as T);
        }
        const encodedPdf = bytesToBase64(pdfBytes);
        await postPatientDocument({
          careseekerName,
          documentType,
          encodedPdf,
          externalId,
          observation,
          on_premise_authorization_token,
          on_premise_domain_ssl,
          targetHTML: encodedPdf,
          trackEvent,
          userId,
        });
        if (!isRedesignActive) {
          toast({
            color: "success",
            message:
              translations.patientForms.transitionalCareForm.downloadSection
                .hisSuccess,
          });
        }
        setProgressSendToHis(QUERY_PROGRESS_NOT_STARTED);
        return true;
      } catch (err) {
        console.error((err as Error).message);
        if (!isRedesignActive) {
          toast({
            color: "danger",
            message:
              translations.patientForms.transitionalCareForm.downloadSection
                .hisError,
          });
        }
        setProgressSendToHis(QUERY_PROGRESS_NOT_STARTED);
        return false;
      }
    },
    [
      bytesToBase64,
      careseekerName,
      createPDFDocument,
      externalId,
      getExistingFileUrl,
      isRedesignActive,
      on_premise_authorization_token,
      on_premise_domain_ssl,
      postPatientDocument,
      isRedesignActive,
      toast,
      trackEvent,
      translations,
      userId,
    ],
  );

  return {
    handleOpenPdf,
    handleSendToHis,
    progressOpenPdf,
    progressSendToHis,
  };
}
